import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Layout from '../../../../components/Layout/Layout'
import { toast } from 'react-toastify'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../context/auth'
import { Modal, Button } from 'antd'
import { saveAs } from 'file-saver'
import AdminMobileCheckListTable from './CheckListTable'
import MobileQtyLog from './MobileQtyLog'
import BoxAddMobileItem from './AddItem'

const AdminMobileCheckList = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const [searchData, setSearchData] = useState('')
  const [focusBarcodeInput, setFocusBarcodeInput] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [visibleLogModal, setVisibleLogModal] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(null)
  const [currentItemDetails, setCurrentItemDetails] = useState({})
  const [editMode, setEditMode] = useState(null)
  const [editField, setEditField] = useState(null)
  const [originalValues, setOriginalValues] = useState({})
  const [filterQuery, setFilterQuery] = useState('')
  const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false)
  const [newItemOnly, setNewItemOnly] = useState(false)

  const barcodeInputRef = useRef(null)
  const [auth] = useAuth()
  const userId = auth?.user?._id

  useEffect(() => {
    const shouldFetch = filterQuery.length > 3 || filterQuery === ''
    if (shouldFetch) {
      fetchData(page)
    }
  }, [page, filterQuery, newItemOnly])

  useEffect(() => {
    if (filterQuery.length > 3 || filterQuery === '') {
      setPage(1)
    }
  }, [filterQuery])

  const requestIdRef = useRef(0)
  const abortRef = useRef(null)

  const fetchData = async (newPage = 1, showToast = false) => {
    // Only the newest request may write to state. Without this, a slow page-3
    // response could land after a page-1 response and append the wrong rows.
    const requestId = ++requestIdRef.current
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/admin/stock-check-mobile/get-mobile-data/${id}`,
        {
          params: {
            userId,
            page: newPage,
            limit: 30,
            search: filterQuery,
            newItemOnly: newItemOnly ? 1 : 0,
          },
          signal: controller.signal,
          timeout: 30000,
        }
      )
      if (requestId !== requestIdRef.current) return
      const { data, pinned, totalPages } = response.data
      if (newPage === 1) {
        // Rows this user touched most recently sit on top. They are dropped
        // from the paged list so nothing shows twice, and later pages are
        // deduped too in case a pinned row turns up again further down.
        const pinnedRows = Array.isArray(pinned) ? pinned : []
        const pinnedIds = new Set(pinnedRows.map((r) => String(r._id)))
        setItems([
          ...pinnedRows,
          ...data.filter((r) => !pinnedIds.has(String(r._id))),
        ])
      } else {
        setItems((prev) => {
          const seen = new Set(prev.map((r) => String(r._id)))
          return [...prev, ...data.filter((r) => !seen.has(String(r._id)))]
        })
      }
      setHasMore(newPage < totalPages)
      if (showToast) {
        toast.success('Data is up to date')
      }
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return
      if (requestId !== requestIdRef.current) return
      console.error('Failed to fetch stock list data:', error)
      // Keep what is already on screen. Blanking the list on a dropped request
      // looked like lost data and invited people to re-scan counted items.
      setHasMore(false)
      toast.error('Could not load data. Check the connection and refresh.')
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false)
    }
  }

  const handleRefresh = () => {
    setPage(1)
    fetchData(1, true)
  }

  useEffect(() => {
    if (focusBarcodeInput) {
      barcodeInputRef.current?.focus()
      setFocusBarcodeInput(false)
    }
  }, [focusBarcodeInput])

  const clearInput = () => {
    setSearchData('')
    setFocusBarcodeInput(true)
  }

  const getSearchContent = (value) => {
    setSearchData(value)
  }

  const handleKeyPress = async (event) => {
    if (event.key === 'Enter' && searchData.trim()) {
      await scanResult()
    }
  }

  const scanResult = async () => {
    try {
      const { data } = await axios.post(
        '/api/v1/admin/stock-check-mobile/scan-mobile-stock',
        { searchData, id, userId }
      )
      if (data.products.length === 0) {
        toast.info('No records found')
      } else {
        updateScannedProduct(data.products[0])
        setLastUpdate(new Date())
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Item not exist in Mobile check list'
      )
    } finally {
      clearInput()
    }
  }

  const updateScannedProduct = (scannedProduct) => {
    const userCode = auth?.user?.code

    setItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) => item._id === scannedProduct._id
      )

      // scannedProduct is the post-increment document from the server, so its
      // checkQty is authoritative. Guessing it locally as "+1" drifted from the
      // DB whenever two people scanned the same item.
      const updatedProduct = {
        ...existingItem,
        ...scannedProduct,
        lastUptByCode: userCode,
        lastUptDate: new Date(),
      }

      // Keep every row already loaded. Dropping the last row to hold the list
      // length fixed hid items until a refresh.
      return [
        updatedProduct,
        ...prevItems.filter((item) => item._id !== scannedProduct._id),
      ]
    })
  }

  const typedQtyRef = useRef({})
  const observer = useRef()
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  const toggleEdit = (itemId, field, currentValue) => {
    const key = `${itemId}-${field}`
    if (editMode === itemId && editField === field) {
      setEditMode(null)
      setEditField(null)
      setItems((currentList) =>
        currentList.map((item) =>
          item._id === itemId
            ? { ...item, [field]: originalValues[key] ?? item[field] }
            : item
        )
      )
    } else {
      setEditMode(itemId)
      setEditField(field)
      // Re-baseline on every open. Keeping the first-ever baseline made the
      // "unchanged" check in saveQty match a stale value and skip the save,
      // leaving the screen and the DB disagreeing.
      setOriginalValues((prev) => ({
        ...prev,
        [key]: currentValue ?? '',
      }))
    }
  }

  const handleQtyKeyPress = (event, itemId, type) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveQty(itemId, type)
    }
  }

  const handleQtyChange = (itemId, value, type) => {
    // Remember the cell was actually typed in, so re-entering the same number
    // still gets saved and logged rather than being skipped as "unchanged".
    typedQtyRef.current[itemId] = true
    setItems((currentList) =>
      currentList.map((item) =>
        item._id === itemId ? { ...item, [type]: value } : item
      )
    )
  }

  const saveQty = async (itemId, type) => {
    const item = items.find((item) => item._id === itemId)
    if (!item) return

    const key = `${itemId}-${type}`
    const previousValue = originalValues[key]
    const raw = String(item.checkQty ?? '').trim()
    const updatedValue = Number(raw)

    const closeEditor = () => {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
    }

    // Blank or non-numeric: restore the old value instead of saving 0.
    if (raw === '' || !Number.isFinite(updatedValue) || updatedValue < 0) {
      setItems((list) =>
        list.map((i) => (i._id === itemId ? { ...i, [type]: previousValue } : i))
      )
      closeEditor()
      toast.error('Quantity must be a number of 0 or more.')
      return
    }

    const wasTyped = typedQtyRef.current[itemId]
    delete typedQtyRef.current[itemId]

    // Only skip the save when the editor was opened and closed untouched.
    if (Number(previousValue) === updatedValue && !wasTyped) {
      closeEditor()
      return
    }

    // Show the new value straight away, then confirm it with the server.
    const updatedProduct = {
      ...item,
      checkQty: updatedValue,
      lastUptByCode: auth?.user?.code,
      lastUptDate: new Date(),
    }
    setItems((prevItems) => [
      updatedProduct,
      ...prevItems.filter((i) => i._id !== itemId),
    ])
    // The row just moved to the top of the list, so follow it there.
    window.scrollTo({ top: 0 })
    closeEditor()

    try {
      await axios.patch(
        `/api/v1/admin/stock-check-mobile/update-mobile-qty/${itemId}`,
        {
          checkQty: updatedValue,
          userId,
        }
      )
    } catch (error) {
      // Roll back so the screen never shows a qty the DB does not hold.
      setItems((prevItems) =>
        prevItems.map((i) =>
          i._id === itemId ? { ...i, checkQty: previousValue } : i
        )
      )
      toast.error(
        error.response?.data?.message || `Failed to update ${type}. Not saved.`
      )
    }
  }

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const handleRemarkChange = (itemId, value) => {
    setItems((currentList) =>
      currentList.map((item) =>
        item._id === itemId ? { ...item, remark: value } : item
      )
    )
  }

  const handleRemarkKeyPress = (event, itemId) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveRemark(itemId)
    }
  }

  const saveRemark = async (itemId) => {
    const item = items.find((i) => i._id === itemId)
    if (!item) return

    const key = `${itemId}-remark`
    const original = (originalValues[key] || '').trim()
    const current = (item.remark || '').trim()

    if (original === current) {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
      return
    }

    // Show it straight away, then confirm with the server.
    const updatedItem = {
      ...item,
      remark: current,
      lastUptByCode: auth?.user?.code,
      lastUptDate: new Date(),
    }
    setItems((prev) => [updatedItem, ...prev.filter((i) => i._id !== itemId)])
    // The row just moved to the top of the list, so follow it there.
    window.scrollTo({ top: 0 })
    setEditMode(null)
    setEditField(null)
    setFocusBarcodeInput(true)

    try {
      await axios.patch(
        `/api/v1/admin/stock-check-mobile/update-mobile-remark/${itemId}`,
        {
          remark: current,
          userId,
        }
      )
    } catch (error) {
      setItems((prev) =>
        prev.map((i) => (i._id === itemId ? { ...i, remark: original } : i))
      )
      toast.error(
        error.response?.data?.message || 'Failed to update remark. Not saved.'
      )
    }
  }

  const openLogModal = (itemId) => {
    const item = items.find((item) => item._id === itemId)
    if (item) {
      setCurrentItemId(itemId)
      setCurrentItemDetails({
        itemCode: item.itemCode,
        description: item.description,
      })
      // Re-stamp so the log refetches on every open. Manual qty saves do not
      // touch lastUpdate, so reopening the same item used to show the list
      // fetched earlier and miss the entry just written.
      setLastUpdate(new Date())
      setVisibleLogModal(true)
    }
  }

  const closeModal = (e) => {
    e.preventDefault()
    setVisibleLogModal(false)
  }

  const exportFile = async () => {
    try {
      const response = await axios.get(
        `/api/v1/admin/stock-check-mobile/export/${id}`,
        { responseType: 'blob' }
      )
      const contentDisposition = response.headers['content-disposition']
      let filename = 'download.xlsx'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch.length === 2) {
          filename = filenameMatch[1]
        }
      }

      saveAs(response.data, filename)
    } catch (error) {
      console.error('Failed to download the file:', error)
      toast.error('Failed to download the file.')
    }
  }

  // Parked: no button wired to this yet, the endpoint exists.
  // eslint-disable-next-line no-unused-vars
  const resetData = async () => {
    const confirmed = window.confirm(
      'Confirm DELETE All Data and Import again?'
    )
    if (confirmed) {
      try {
        await axios.delete(`/api/v1/admin/stock-check-mobile/reset-data/${id}`)
        toast.success('Data reset successfully!')
        fetchData(1) // Refetch data after reset
      } catch (error) {
        toast.error('Failed to reset data.')
        console.error('Failed to reset data:', error)
      }
    }
  }

  const handleItemAdded = (newItem) => {
    setItems((prevItems) => [newItem, ...prevItems])
  }

  return (
    <Layout title='Mobile Check List'>
      {/* Normal flow, not position:fixed with a hard-coded 120px height - the
          toolbar wraps on a narrow screen and a fixed bar could not grow
          without covering the first rows of the table. */}
      <div
        className='shadow-sm border-bottom px-3 px-md-4 pt-3 pb-3 bg-white'
        // sticky, not fixed: it still sits in the flow so it can wrap to
        // another row without covering the table, but stays put on scroll.
        // top clears the fixed navbar. z-index must stay BELOW antd's modal
        // base of 1000, or the bar paints over the log / Add Item dialogs.
        style={{ position: 'sticky', top: '50px', zIndex: 900 }}
      >
        <div className='d-flex align-items-center flex-wrap gap-3 mb-3'>
          {/* Breadcrumb instead of a "Return to Main Page" button - the step
              names where it goes, and the last crumb is the location, so the
              separate centred title is no longer needed. */}
          <nav aria-label='breadcrumb' className='me-auto'>
            <ol className='breadcrumb wms-breadcrumb mb-0'>
              <li className='breadcrumb-item'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-none'
                  onClick={() =>
                    navigate(`/admin/stock-check/details/${id}`, {
                      state: { id: id },
                    })
                  }
                >
                  Stock Check
                </button>
              </li>
              <li className='breadcrumb-item active' aria-current='page'>
                <span className='wms-crumb-current'>Mobile</span>
              </li>
            </ol>
          </nav>

          <div className='d-flex'>
            <input
              type='text'
              className='form-control'
              style={{ width: '250px', maxWidth: '100%' }}
              placeholder='Search Item Code or Desc'
              onChange={(e) => handleFilterChange(e.target.value)}
              value={filterQuery}
            />
            <button
              className='btn btn-secondary'
              onClick={() => setFilterQuery('')}
            >
              Clear
            </button>
          </div>

          <button
            type='button'
            className='btn btn-info'
            onClick={handleRefresh}
            aria-label='Refresh'
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>

        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='d-flex flex-grow-1' style={{ minWidth: '240px' }}>
            <input
              type='text'
              className='form-control'
              placeholder='Scan / Enter Item Code'
              onChange={(e) => getSearchContent(e.target.value)}
              onKeyDown={handleKeyPress}
              value={searchData}
              style={{ flex: 1 }}
              autoFocus
              ref={barcodeInputRef}
            />
            <button
              type='button'
              className='btn btn-secondary'
              disabled={!searchData}
              onClick={clearInput}
            >
              Clear
            </button>
          </div>

          <div className='form-check d-flex align-items-center'>
            <input
              className='form-check-input'
              type='checkbox'
              id='newItemOnly'
              checked={newItemOnly}
              onChange={(e) => setNewItemOnly(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label
              className='form-check-label ms-1'
              htmlFor='newItemOnly'
              style={{ cursor: 'pointer' }}
            >
              New Item
            </label>
          </div>

          <div className='d-flex flex-wrap gap-3'>
            <button
              type='button'
              className='btn btn-success'
              onClick={() => exportFile()}
            >
              Export Mobile
            </button>

            <button
              type='button'
              className='btn btn-warning'
              onClick={() =>
                navigate(`/admin/stock-check/mobile-mismatch-qty/${id}`)
              }
            >
              Mismatch / Remarks
            </button>
            {/* <button
            type='button'
            className='btn btn-danger'
            onClick={resetData}
            disabled
          >
            <i className='bi bi-exclamation-triangle-fill'></i> Reset All Data
          </button> */}
            <button
              type='button'
              className='btn btn-wms-add'
              onClick={() => setIsAddItemModalVisible(true)}
            >
              Add Item
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .wms-breadcrumb {
          --bs-breadcrumb-divider: '\\203A';
          font-size: 0.95rem;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item {
          display: flex;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
          color: #adb5bd;
          padding: 0 0.6rem;
          font-size: 1.1rem;
        }
        .wms-breadcrumb .btn-link {
          color: #6c757d;
          font-weight: 500;
          vertical-align: baseline;
        }
        .wms-breadcrumb .btn-link:hover,
        .wms-breadcrumb .btn-link:focus-visible {
          color: rgb(47, 68, 131);
        }
        .wms-crumb-current {
          font-size: 1.15rem;
          font-weight: 700;
          color: #198754;
        }
      `}</style>

      <div>
        <div className='table-responsive'>
          <AdminMobileCheckListTable
            items={items}
            lastElementRef={lastElementRef}
            editMode={editMode}
            editField={editField}
            handleQtyChange={handleQtyChange}
            saveQty={saveQty}
            handleQtyKeyPress={handleQtyKeyPress}
            toggleEdit={toggleEdit}
            openLogModal={openLogModal}
            handleRemarkChange={handleRemarkChange}
            handleRemarkKeyPress={handleRemarkKeyPress}
            saveRemark={saveRemark}
            isLoading={isLoading}
            currentPage={page}
            onItemDeleted={(deletedId) =>
              setItems((prev) => prev.filter((item) => item._id !== deletedId))
            }
          />
        </div>
      </div>

      <Modal
        closable={false}
        onCancel={() => setVisibleLogModal(false)}
        open={visibleLogModal}
        width={'70%'}
        footer={[
          <Button key='cancel' onClick={() => setVisibleLogModal(false)}>
            Cancel
          </Button>,
        ]}
      >
        <MobileQtyLog
          closeLogModal={closeModal}
          itemId={currentItemId}
          itemDetails={currentItemDetails}
          lastUpdate={lastUpdate}
        />
      </Modal>

      <BoxAddMobileItem
        id={id}
        isModalVisible={isAddItemModalVisible}
        hideModal={() => setIsAddItemModalVisible(false)}
        onItemAdded={handleItemAdded}
      />
    </Layout>
  )
}

export default AdminMobileCheckList
